import type {
  NsurePlanOverview,
  QuestionnaireAnswerItemDto,
  QuestionnaireQuestionDto,
  QuestionnaireQuestionItemDto,
  SaveAnswerItem,
} from "Infrastructure/ApiZfp/Schema";
import { QuestionnaireStatus } from "Infrastructure/ApiZfp/Schema";
import { addYears } from "date-fns";
import type { InvestmentPlan } from "./types";
import { type RuleState, computeRuleState } from "./useRuleEngine";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isQuestionsModifiedStatus(status: QuestionnaireStatus): boolean {
  return status === QuestionnaireStatus.questions_modified || status === QuestionnaireStatus.nsure_questions_changed;
}

export function isExpiredStatus(status: QuestionnaireStatus): boolean {
  return status === QuestionnaireStatus.expired;
}

/** Vyhodnocení z 29. 2. platí do 28. 2. následujícího roku (date-fns ořízne den na konec měsíce). */
export function getQuestionnaireValidUntil(evaluatedAt: Date): Date {
  return addYears(evaluatedAt, 1);
}

/** Vyhodnocení není platné a dotazník je nutné vyplnit znovu (změna otázek nebo vypršená platnost). */
export function isReEvaluationRequiredStatus(status: QuestionnaireStatus): boolean {
  return isQuestionsModifiedStatus(status) || isExpiredStatus(status);
}

export function mapAnswerPayloadToFormData(items: QuestionnaireAnswerItemDto[]): Record<string, any> {
  const result: Record<string, any> = {};

  // Group by (questionnaireQuestionID, investmentPlanExternalID) to detect child groups:
  // when the same (questionID, planID) has both isSelected=null and isSelected=true items,
  // the null one is the main select (X_uuid) and the true ones are child group (X_childGroup_uuid).
  const grouped = new Map<string, QuestionnaireAnswerItemDto[]>();
  for (const item of items) {
    if (item.questionnaireQuestionID == null) continue;
    const groupKey = `${item.questionnaireQuestionID}__${item.investmentPlanExternalID ?? ""}`;
    if (!grouped.has(groupKey)) grouped.set(groupKey, []);
    grouped.get(groupKey)!.push(item);
  }

  for (const groupItems of grouped.values()) {
    const qID = groupItems[0].questionnaireQuestionID!;
    const planID = groupItems[0].investmentPlanExternalID ?? null;

    const singleItems = groupItems.filter((i) => i.isSelected !== true && i.questionnaireQuestionItemID != null && !i.answerText);
    const multiItems = groupItems.filter((i) => i.isSelected === true && i.questionnaireQuestionItemID != null && !i.answerText);
    const valueItems = groupItems.filter((i) => i.answerText && i.questionnaireQuestionItemID != null);
    const textOnlyItems = groupItems.filter((i) => i.answerText && i.questionnaireQuestionItemID == null);

    if (planID) {
      // Rule 2 single: X_uuid = String(itemID)
      for (const item of singleItems) {
        result[`${qID}_${planID}`] = String(item.questionnaireQuestionItemID!);
      }

      if (multiItems.length > 0) {
        const multiValues = multiItems.map((i) => String(i.questionnaireQuestionItemID!));
        if (singleItems.length > 0) {
          // Child group coexists with single select → X_childGroup_uuid
          result[`${qID}_childGroup_${planID}`] = multiValues;
        } else {
          // Standalone multi-select → X_uuid = array
          result[`${qID}_${planID}`] = multiValues;
        }
      }

      // Rule 4: X_Y_uuid___Value
      for (const item of valueItems) {
        result[`${qID}_${item.questionnaireQuestionItemID}_${planID}___Value`] = item.answerText;
      }
    } else {
      // Rule 1 single: X = String(itemID)
      for (const item of singleItems) {
        result[String(qID)] = String(item.questionnaireQuestionItemID!);
      }

      // Rule 1 multi: X = array
      if (multiItems.length > 0) {
        result[String(qID)] = multiItems.map((i) => String(i.questionnaireQuestionItemID!));
      }

      // Rule 5: X___Value
      for (const item of textOnlyItems) {
        result[`${qID}___Value`] = item.answerText;
      }
    }
  }

  return result;
}

export function mapFormDataToAnswerPayload(formData: Record<string, any>): SaveAnswerItem[] {
  const result: SaveAnswerItem[] = [];

  for (const [key, rawValue] of Object.entries(formData)) {
    if (rawValue === undefined || rawValue === null) continue;

    const isArrayValue = Array.isArray(rawValue);
    const hasValueSuffix = key.endsWith("___Value");
    const baseKey = hasValueSuffix ? key.slice(0, -"___Value".length) : key;
    const normalizedKey = baseKey.replace("_childGroup", "");
    const parts = normalizedKey.split("_");

    const values: string[] = isArrayValue ? rawValue : [rawValue];

    for (const value of values) {
      if (value === undefined || value === null) continue;

      const isSelected = isArrayValue ? true : null;

      // Rule 1: X
      if (parts.length === 1 && !hasValueSuffix) {
        result.push({
          questionnaireQuestionID: Number(parts[0]),
          questionnaireQuestionItemID: Number(value),
          investmentPlanExternalID: null,
          answerText: "",
          isSelected,
        });
      }
      // Rule 5: X___Value
      else if (parts.length === 1 && hasValueSuffix) {
        result.push({
          questionnaireQuestionID: Number(parts[0]),
          questionnaireQuestionItemID: null,
          investmentPlanExternalID: null,
          answerText: String(value),
          isSelected,
        });
      }
      // Rule 2: X_uuid
      else if (parts.length === 2 && UUID_REGEX.test(parts[1]) && !hasValueSuffix) {
        result.push({
          questionnaireQuestionID: Number(parts[0]),
          questionnaireQuestionItemID: Number(value),
          investmentPlanExternalID: parts[1],
          answerText: "",
          isSelected,
        });
      }
      // Rule 3: X_Y_uuid
      else if (parts.length === 3 && UUID_REGEX.test(parts[2]) && !hasValueSuffix) {
        result.push({
          questionnaireQuestionID: Number(parts[0]),
          questionnaireQuestionItemID: Number(parts[1]),
          investmentPlanExternalID: parts[2],
          answerText: "",
          isSelected,
        });
      }
      // Rule 4: X_Y_uuid___Value
      else if (parts.length === 3 && UUID_REGEX.test(parts[2]) && hasValueSuffix) {
        result.push({
          questionnaireQuestionID: Number(parts[0]),
          questionnaireQuestionItemID: Number(parts[1]),
          investmentPlanExternalID: parts[2],
          answerText: String(value),
          isSelected,
        });
      }
    }
  }

  return result;
}

function fillChildItems(option: QuestionnaireQuestionItemDto, parentFieldName: string, result: Record<string, any>): void {
  if (!option.childrenQuestionItems || option.childrenQuestionItems.length === 0) return;

  const isPlanSpecific = parentFieldName.includes("_");
  const parts = parentFieldName.split("_");
  const planExternalId = isPlanSpecific ? parts[1] : null;
  const parentQuestionId = isPlanSpecific ? parts[0] : null;

  const multiCheckboxChildren = option.childrenQuestionItems.filter((c) => c.type === "MultiCheckbox");
  if (multiCheckboxChildren.length > 0) {
    const childGroupFieldName = isPlanSpecific
      ? `${parentQuestionId}_childGroup_${planExternalId}`
      : `childGroup_${option.questionnaireQuestionItemID}`;
    if (result[childGroupFieldName] === undefined) {
      const shuffled = [...multiCheckboxChildren].sort(() => Math.random() - 0.5);
      const count = 1 + Math.floor(Math.random() * shuffled.length);
      result[childGroupFieldName] = shuffled.slice(0, count).map((c) => c.questionnaireQuestionItemID.toString());
    }
  }

  for (const childItem of option.childrenQuestionItems) {
    if (childItem.type === "MultiCheckbox") continue;
    const childFieldName = isPlanSpecific
      ? `${parentQuestionId}_${childItem.questionnaireQuestionItemID}_${planExternalId}`
      : childItem.questionnaireQuestionItemID.toString();
    if (childItem.type === "Number") {
      const key = `${childFieldName}___Value`;
      if (result[key] === undefined) result[key] = String(10 + Math.floor(Math.random() * 80));
    } else if (childItem.type === "Text") {
      const key = `${childFieldName}___Value`;
      if (result[key] === undefined) result[key] = `Test ${Math.floor(Math.random() * 100)}`;
    }
  }
}

function fillSingleQuestion(q: QuestionnaireQuestionDto, fieldName: string, ruleState: RuleState, result: Record<string, any>): void {
  if (result[fieldName] !== undefined || result[`${fieldName}___Value`] !== undefined) return;

  const availableItems = q.questionnaireQuestionItems.filter(
    (item) => item.parentQuestionItemID === null && !ruleState.isItemDisabled(item.questionnaireQuestionItemID),
  );

  switch (q.type) {
    case "Select":
    case "SelectWithImage":
    case "Dropdown":
    case "Checkbox": {
      if (availableItems.length === 0) return;
      const chosen = availableItems[Math.floor(Math.random() * availableItems.length)];
      result[fieldName] = chosen.questionnaireQuestionItemID.toString();
      fillChildItems(chosen, fieldName, result);
      break;
    }
    case "MultiCheckbox":
    case "MultiSelect":
    case "MultiDropdown": {
      if (availableItems.length === 0) return;
      const shuffled = [...availableItems].sort(() => Math.random() - 0.5);
      const count = 1 + Math.floor(Math.random() * shuffled.length);
      result[fieldName] = shuffled.slice(0, count).map((i) => i.questionnaireQuestionItemID.toString());
      break;
    }
    case "Text":
    case "TextArea": {
      result[`${fieldName}___Value`] = `Test ${Math.floor(Math.random() * 1000)}`;
      break;
    }
  }
}

export function generateRandomAnswers(
  allQuestions: QuestionnaireQuestionDto[],
  currentGroupQuestions: QuestionnaireQuestionDto[],
  investmentPlans: InvestmentPlan[],
  collectedData: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};

  // Iterate until stable — each round can reveal new visible questions via rule engine
  for (let iteration = 0; iteration < 10; iteration++) {
    const before = JSON.stringify(result);
    const ruleState = computeRuleState(allQuestions, collectedData, result);

    for (const q of currentGroupQuestions) {
      if (q.parentQuestionID !== null) continue;

      if (q.subType === "InvestmentPlan") {
        const relatedQuestions = allQuestions.filter((rq) => rq.parentQuestionID === q.questionnaireQuestionID);
        for (const plan of investmentPlans) {
          if (!ruleState.isQuestionVisible(q.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId)) continue;
          for (const relQ of relatedQuestions) {
            if (!ruleState.isQuestionVisible(relQ.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId)) continue;
            fillSingleQuestion(relQ, `${relQ.questionnaireQuestionID}_${plan.nsureInvestmentPlanExternalId}`, ruleState, result);
          }
        }
        continue;
      }

      if (q.questionnaireQuestionItems.length === 0 && q.type !== "Text") {
        const relatedQuestions = allQuestions.filter((rq) => rq.parentQuestionID === q.questionnaireQuestionID);
        for (const relQ of relatedQuestions) {
          if (!ruleState.isQuestionVisible(relQ.questionnaireQuestionID, null)) continue;
          fillSingleQuestion(relQ, relQ.questionnaireQuestionID.toString(), ruleState, result);
        }
        continue;
      }

      if (q.subType === "InvestmentOnly") {
        for (const plan of investmentPlans) {
          fillSingleQuestion(q, `${q.questionnaireQuestionID}_${plan.nsureInvestmentPlanExternalId}`, ruleState, result);
        }
        continue;
      }

      if (!ruleState.isGroupVisible(q.questionnaireGroupID)) continue;
      if (!ruleState.isQuestionVisible(q.questionnaireQuestionID, null)) continue;

      fillSingleQuestion(q, q.questionnaireQuestionID.toString(), ruleState, result);
    }

    if (JSON.stringify(result) === before) break;
  }

  return result;
}

export const mergeFormData = (base: Record<string, any>, updates: Record<string, any>): Record<string, any> => ({
  ...base,
  ...updates,
});

const normalizeAnswerValue = (value: any): string | string[] | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    return [...value].map(String).sort();
  }
  return String(value);
};

export function haveAnswersChanged(original: Record<string, any>, current: Record<string, any>): boolean {
  const keys = new Set([...Object.keys(original), ...Object.keys(current)]);
  for (const key of keys) {
    const originalValue = normalizeAnswerValue(original[key]);
    const currentValue = normalizeAnswerValue(current[key]);
    if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) return true;
  }
  return false;
}

export function pruneHiddenAnswers(
  allQuestions: QuestionnaireQuestionDto[],
  investmentPlans: NsurePlanOverview[],
  data: Record<string, any>,
): Record<string, any> {
  const ruleState = computeRuleState(allQuestions, {}, data);
  const result = { ...data };

  const removeKeys = (q: QuestionnaireQuestionDto, externalId: string | null) => {
    const isVisible =
      ruleState.isGroupVisible(q.questionnaireGroupID) && ruleState.isQuestionVisible(q.questionnaireQuestionID!, externalId);
    const base = externalId ? `${q.questionnaireQuestionID}_${externalId}` : `${q.questionnaireQuestionID}`;
    const currentValue = result[base];

    if (!isVisible) {
      const suffix = q.type === "Text" ? "___Value" : "";
      const mainKey = `${base}${suffix}`;
      delete result[mainKey];

      for (const item of q.questionnaireQuestionItems ?? []) {
        if (item.childrenQuestionItems.length > 0) {
          const childGroupKey = externalId
            ? `${q.questionnaireQuestionID}_childGroup_${externalId}`
            : `childGroup_${item.questionnaireQuestionItemID}`;
          delete result[childGroupKey];
        }
        for (const child of item.childrenQuestionItems) {
          const childBase = externalId
            ? `${q.questionnaireQuestionID}_${child.questionnaireQuestionItemID}_${externalId}`
            : `${child.questionnaireQuestionItemID}`;
          delete result[childBase];
          delete result[`${childBase}___Value`];
        }
      }
      return;
    }

    // disabled answers
    if (Array.isArray(currentValue)) {
      const filtered = currentValue.filter((id: string) => !ruleState.isItemDisabled(Number(id)));
      if (filtered.length === 0) delete result[base];
      else result[base] = filtered;
    } else if (typeof currentValue === "string" && ruleState.isItemDisabled(Number(currentValue))) {
      delete result[base];
    }
  };

  for (const q of allQuestions) {
    const parent = allQuestions.find((p) => p.questionnaireQuestionID === q.parentQuestionID);

    if (parent?.subType === "InvestmentPlan") {
      for (const plan of investmentPlans) removeKeys(q, plan.nsureInvestmentPlanExternalId);
    } else if (q.subType === "InvestmentOnly") {
      for (const plan of investmentPlans) removeKeys(q, plan.nsureInvestmentPlanExternalId);
    } else {
      removeKeys(q, null);
    }
  }

  return result;
}
